import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import useFetch from '../../hooks/useFetch';
import 'react-datepicker/dist/react-datepicker.css';
import DatePicker from 'react-datepicker';

import MainLayout from '../../layouts/MainLayout';
import { Bar } from 'react-chartjs-2';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement
);
import orgImage from '../../assets/pageimages/suklogo.webp';

const InspireDashboard = () => {
    const authState = useSelector(state => state.authReducer);
    const navigate = useNavigate();
    const [fetchData, { loading }] = useFetch();
    const { userId, setUserId } = useState(authState.user._id);
    const [isFilterClicked, setIsFilterClicked] = useState(false);
    const [student, setStudent] = useState('');
    const [teacher, setTeacher] = useState('');
    const [principal, setPrincipal] = useState('');
    const [admin, setAdmin] = useState('');
    const [session, setSession] = useState('');
    const [studentClass, setStudentClass] = useState('');
    const [section, setSection] = useState('');
    const [branch, setBranch] = useState('');
    const [department, setDepartment] = useState('');
    const [state, setState] = useState('');
    const [country, setCountry] = useState('');
    const [fromDate, setFromDate] = useState(new Date());
    const [toDate, setToDate] = useState(new Date());
    const [error, setError] = useState(null);
    const [dashboardData, setDashboardData] = useState([]);
    const [barChartData, setBarChartData] = useState({ labels: [], datasets: [] });
    const [lineChartData, setLineChartData] = useState({ labels: [], datasets: [] });
    const [isDummyClicked, setIsDummyClicked] = useState(false);
    const [studentClassMetrics, setStudentClassMetrics] = useState([]);
    const [branchMetrics, setBranchMetrics] = useState([]);
    const [teacherMetrics, setTeacherMetrics] = useState([]);
    const [departmentDMetrics, setDepartmentDMetrics] = useState([]);

    const [metrics, setMetrics] = useState([
      { title: "Total RCAs Conducted", value: "0", icon: "faClipboardCheck" },
      { title: "Pending RCAs", value: "0", icon: "faExclamationCircle" },
      { title: "Recurring Issues", value: "0", icon: "faRedoAlt" },
      { title: "Avg. Resolution Time", value: "0 Days", icon: "faClock" },
    ]);

    const fetchDashboardData = useCallback(() => {
      const tFromDate = fromDate;
      tFromDate.setHours(0, 0, 0, 0);
      const tLocalFromDate = new Date(tFromDate.getTime() - (tFromDate.getTimezoneOffset() * 60000));
      const tFormattedFromDate = tLocalFromDate.toISOString().split('T')[0];
      
      const tToDate = toDate;
      tToDate.setHours(0, 0, 0, 0);
      const tLocalToDate = new Date(tToDate.getTime() - (tToDate.getTimezoneOffset() * 60000));
      const tFormattedToDate = tLocalToDate.toISOString().split('T')[0];

      const config = {
        url: "/rca/inspiredashboarddata",
        method: "post",
        headers: { Authorization: authState.token },
        data: {
          userId: userId,
          student,
          teacher,
          principal,
          admin,
          session,
          studentClass,
          section,
          branch,
          state,
          country,
          fromDate: tFormattedFromDate,
          toDate: tFormattedToDate,
        },
      };

      fetchData(config, { showSuccessToast: false })
        .then((response) => {
          if (response && response.dashboardData) {
            // console.log("Page Content ",response);
            // Metrics Aggregation calculations
            const totalRCAs = response.dashboardData.length;
            const pendingRCAs = response.dashboardData.filter((item) => !item.isCompleted).length;
            const recurringIssues = response.dashboardData.filter((item) => item.isRecurring).length;
            const avgResolutionTime =
              response.dashboardData.reduce((acc, item) => {
                const fromDate = new Date(item.rcaFromDate);
                const toDate = new Date(item.rcaToDate);
                return acc + (toDate - fromDate) / (1000 * 60 * 60 * 24); // Convert ms to days
              }, 0) / (response.dashboardData.length || 1); // Avoid divide by 0

            // Update metrics
            setMetrics([
              { title: "Total RCAs Conducted", value: totalRCAs, icon: "faClipboardCheck" },
              { title: "Pending RCAs", value: pendingRCAs, icon: "faExclamationCircle" },
              { title: "Recurring Issues", value: recurringIssues, icon: "faRedoAlt" },
              { title: "Avg. Resolution Time", value: `${avgResolutionTime.toFixed(1)} Days`, icon: "faClock" },
            ]);

            // Student Class Aggregation calculations
            const scmetrics = response.dashboardData.reduce((acc, scitem) => {
              const fromDate = new Date(scitem.rcaFromDate);
              const toDate = new Date(scitem.rcaToDate);
              const resolutionTime = (toDate - fromDate) / (1000 * 60 * 60 * 24); // Calculate resolution time in days
              if (!acc[scitem.studentClass]) {
                acc[scitem.studentClass] = {
                  studentClass: scitem.studentClass,
                  totalIssues: 0,
                  resolved: 0,
                  pending: 0,
                  totalResolutionTime: 0,
                };
              }
              acc[scitem.studentClass].totalIssues += 1;
              acc[scitem.studentClass].resolved += scitem.isCompleted ? 1 : 0;
              acc[scitem.studentClass].pending += scitem.isCompleted ? 0 : 1;
              acc[scitem.studentClass].totalResolutionTime += resolutionTime;

              return acc;
            }, {}); 
            // console.log(dmetrics);

            const aggregatedMetrics = Object.values(scmetrics).map((sc) => ({
              studentClass: sc.studentClass,
              totalIssues: sc.totalIssues,
              resolved: sc.resolved,
              pending: sc.pending,
              avgResolutionTime: (sc.totalResolutionTime / sc.totalIssues).toFixed(1),
            }));
            // console.log(aggregatedMetrics);
            setStudentClassMetrics(aggregatedMetrics);


            // Branch / College Aggregation calculations
            const bmetrics = response.dashboardData.reduce((acc, bitem) => {
              const fromDate = new Date(bitem.rcaFromDate);
              const toDate = new Date(bitem.rcaToDate);
              const resolutionTime = (toDate - fromDate) / (1000 * 60 * 60 * 24); // Calculate resolution time in days
              if (!acc[bitem.branch]) {
                acc[bitem.branch] = {
                  branch: bitem.branch,
                  totalIssues: 0,
                  resolved: 0,
                  pending: 0,
                  totalResolutionTime: 0,
                };
              }
              acc[bitem.branch].totalIssues += 1;
              acc[bitem.branch].resolved += bitem.isCompleted ? 1 : 0;
              acc[bitem.branch].pending += bitem.isCompleted ? 0 : 1;
              acc[bitem.branch].totalResolutionTime += resolutionTime;

              return acc;
            }, {}); 
            // console.log(dmetrics);

            const aggregatedBMetrics = Object.values(bmetrics).map((b) => ({
              branch: b.branch,
              totalIssues: b.totalIssues,
              resolved: b.resolved,
              pending: b.pending,
              avgResolutionTime: (b.totalResolutionTime / b.totalIssues).toFixed(1),
            }));
            // console.log(aggregatedBMetrics);
            setBranchMetrics(aggregatedBMetrics);

            // Department Aggregation calculations
            const dmetrics = response.dashboardData.reduce((acc, ditem) => {
              const fromDate = new Date(ditem.rcaFromDate);
              const toDate = new Date(ditem.rcaToDate);
              const resolutionTime = (toDate - fromDate) / (1000 * 60 * 60 * 24); // Calculate resolution time in days
              if (!acc[ditem.department]) {
                acc[ditem.department] = {
                  departmentName: ditem.department,
                  totalIssues: 0,
                  resolved: 0,
                  pending: 0,
                  totalResolutionTime: 0,
                };
              }
              acc[ditem.department].totalIssues += 1;
              acc[ditem.department].resolved += ditem.isCompleted ? 1 : 0;
              acc[ditem.department].pending += ditem.isCompleted ? 0 : 1;
              acc[ditem.department].totalResolutionTime += resolutionTime;

              return acc;
            }, {}); 
            // console.log(dmetrics);

            const aggregatedDMetrics = Object.values(dmetrics).map((d) => ({
              departmentName: d.departmentName,
              totalIssues: d.totalIssues,
              resolved: d.resolved,
              pending: d.pending,
              avgResolutionTime: (d.totalResolutionTime / d.totalIssues).toFixed(1),
            }));
            // console.log(aggregatedDMetrics);
            setDepartmentDMetrics(aggregatedDMetrics);

            // Teacher Aggregation calculations
            const tmetrics = response.dashboardData.reduce((acc, titem) => {
              const fromDate = new Date(titem.rcaFromDate);
              const toDate = new Date(titem.rcaToDate);
              const resolutionTime = (toDate - fromDate) / (1000 * 60 * 60 * 24); // Calculate resolution time in days
              if (!acc[titem.teacher]) {
                acc[titem.teacher] = {
                  teacher: titem.teacher,
                  totalIssues: 0,
                  resolved: 0,
                  pending: 0,
                  totalResolutionTime: 0,
                };
              }
              acc[titem.teacher].totalIssues += 1;
              acc[titem.teacher].resolved += titem.isCompleted ? 1 : 0;
              acc[titem.teacher].pending += titem.isCompleted ? 0 : 1;
              acc[titem.teacher].totalResolutionTime += resolutionTime;

              return acc;
            }, {}); 
            // console.log(tmetrics);

            const aggregatedTMetrics = Object.values(tmetrics).map((t) => ({
              teacher: t.teacher,
              totalIssues: t.totalIssues,
              resolved: t.resolved,
              pending: t.pending,
              avgResolutionTime: (t.totalResolutionTime / t.totalIssues).toFixed(1),
            }));
            // console.log(aggregatedTMetrics);
            setTeacherMetrics(aggregatedTMetrics);

            const newBarChartData = generateBarChartData(response.dashboardData);
            setBarChartData(newBarChartData);
            const newLineChartData = generateLineChartData(response.dashboardData);
            // console.log("newLineChartData ",newLineChartData);

            setLineChartData(newLineChartData);

            setDashboardData(response.dashboardData);
            setError(null);
          } else {
            console.error(
              "Invalid response format: data not found in response"
            );
          }
        })
        .catch((error) => {
          console.error("Error fetching INSPIRE dashboard data:", error);
        });
    }, [
      authState.token,
      student,
      teacher,
      principal,
      admin,
      session,
      studentClass,
      section,
      branch,
      state,
      country,
      fromDate,
      toDate,
      fetchData,
    ]);

    const processMonthlyData = (data) => {
      const monthData = {};
    // console.log("processMonthlyData ",data);
      data.forEach(item => {
        const rcaFromDate = new Date(item.rcaFromDate);
        const rcaToDate = new Date(item.rcaToDate);
        const monthKey = `${rcaFromDate.getFullYear()}-${rcaFromDate.getMonth() + 1}`; // 'YYYY-MM' format
        const resolutionTime = (rcaToDate - rcaFromDate) / (1000 * 3600 * 24); 
        if (!monthData[monthKey]) {
          monthData[monthKey] = {
            issuesRaised: 0,
            issuesResolved: 0,
            totalResolutionTime: 0,
            count: 0,
          };
        }
    
        monthData[monthKey].issuesRaised += 1;
        if (item.isCompleted) {
          monthData[monthKey].issuesResolved += 1;
        }
        monthData[monthKey].totalResolutionTime += resolutionTime;
        monthData[monthKey].count += 1;
      });
      const monthlyDataArray = Object.keys(monthData).map(monthKey => {
        const { issuesRaised, issuesResolved, totalResolutionTime, count } = monthData[monthKey];
        return {
          month: monthKey,
          issuesRaised,
          issuesResolved,
          avgResolutionTime: totalResolutionTime / count || 0, 
        };
      });
      return monthlyDataArray;
    };
    
    const generateBarChartData = (data) => {
      if (!Array.isArray(data) || data.length === 0) {
        console.error("Invalid or empty data passed to generateBarChartData", data);
        return {
          labels: [],
          datasets: [],
        };
      }
      // console.log("generateBarChartData: ",data);

      const monthlyData = processMonthlyData(data);
      if (!Array.isArray(monthlyData) || monthlyData.length === 0) {
        console.error("Processed monthly data is invalid or empty", monthlyData);
        return {
          labels: [],
          datasets: [],
        };
      }

      const labels = monthlyData.map(item => item.month);
      const issuesResolved = monthlyData.map(item => item.issuesResolved);
      const issuesRaised = monthlyData.map(item => item.issuesRaised);
      // console.log("labels: ",labels);
      // console.log("issuesResolved  : ",issuesResolved);

      // console.log("issuesRaised  : ",issuesRaised);
      return {
        labels: labels,
        datasets: [
          {
            label: 'Issues Resolved',
            data: issuesResolved,
            backgroundColor: 'rgba(75, 192, 192, 0.5)',
          },
          {
            label: 'Issues Raised',
            data: issuesRaised,
            backgroundColor: 'rgba(255, 99, 132, 0.5)',
          },
        ],
      };
    };
    
    const generateLineChartData = (data) => {
      const monthlyData = processMonthlyData(data);
      if (!Array.isArray(monthlyData) || monthlyData.length === 0) {
        console.error("Invalid or empty monthly data", monthlyData);
        return {
          labels: [],
          datasets: [],
        };
      }
      const labels = monthlyData.map(item => item.month);
      const avgResolutionTime = monthlyData.map(item => item.avgResolutionTime);

      return {
        labels: labels,
        datasets: [
          {
            label: 'Average Resolution Time (days)',
            data: avgResolutionTime,
            borderColor: 'rgba(54, 162, 235, 1)',
            backgroundColor: 'rgba(54, 162, 235, 0.2)',
            fill: true,
            tension: 0.4,
          },
        ],
      };
  };

  const dummyBarChartData = {
      labels: ['January', 'February', 'March', 'April', 'May'],
      datasets: [
          {
          label: 'Issues Resolved',
          data: [40, 55, 75, 60, 90],
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          },
          {
          label: 'Issues Raised',
          data: [50, 60, 80, 70, 95],
          backgroundColor: 'rgba(255, 99, 132, 0.5)',
          },
      ],
    };

    const dummyLineChartData = {
    labels: ['January', 'February', 'March', 'April', 'May'],
    datasets: [
        {
        label: 'Average Resolution Time (days)',
        data: [5, 6, 4, 5, 3],
        borderColor: 'rgba(54, 162, 235, 1)',
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        fill: true,
        tension: 0.4,
        },
    ],
  };

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
          legend: {
          position: 'top',
          },
          title: {
          display: true,
          text: 'Chart Title',
          },
      },
    };

  return (
    <div className="min-h-screen bg-gray-900 p-4 [&_p]:text-white">
      <header className="bg-black border-b border-blue-400 text-white py-6 px-8 rounded-md shadow-md mb-6">
        <div className="flex flex-col md:flex-row items-center">
            <div className="flex-shrink-0 mb-4 md:mb-0 md:mr-6">
            <img 
                src={orgImage} 
                alt="Shivaji University Kolhapur Logo" 
                className="w-24 h-24 object-contain" 
            />
            </div>
            <div className="text-left">
            <h1 className="tet-xl lg:text-2xl text-left font-bold mb-2">Shivaji University, Kolhapur | शिवाजी विद्यापीठ, कोल्हापूर</h1>
            <p className="text-sm mb-1">
                SUK INSPIRE: Shivaji University Kolhaput Innovative System for Progressive Institutional Reform and Excellence
            </p>
            <p className="text-xs italic">
                This page is not an official page. It is for example purposes only.
            </p>
            </div>
        </div>
      </header>

      <div className="flex flex-col items-center gap-2 p-4">
        <div className='flex flex-row gap-2'>
          <button
            className="w-64 bg-blue-600 text-white py-2 px-4 rounded-lg text-sm transition hover:bg-blue-700 focus:ring-2 focus:ring-blue-400 focus:outline-none"
            onClick={() => setIsFilterClicked(!isFilterClicked)}
          >
            {isFilterClicked ? "Hide Filter" : "Show Filter"}
          </button>

          <button
            className={`${
              isDummyClicked ? "bg-green-800" : "bg-blue-600"
            } w-64 text-white py-2 px-4 rounded-lg text-sm transition hover:bg-blue-700 focus:ring-2 focus:ring-blue-400 focus:outline-none`}
            onClick={() => setIsDummyClicked(!isDummyClicked)}
          >
            {isDummyClicked ? "Hide Sample" : "Show Sample"}
          </button>
          <button className="bg-blue-600 w-40 text-white" onClick={() => navigate("/dashboard")}>Dashboard</button>
        </div>

        {isFilterClicked && !isDummyClicked && (
          <div className="w-full max-w-4xl bg-gray-600 text-white p-6 rounded-lg shadow-lg mt-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 text-white">
              <div className="flex flex-col mr-12">
                  <label className="text-sm font-semibold text-white mb-1">From Date:</label>
                  <DatePicker
                    selected={fromDate}
                    onChange={(date) => setFromDate(date)}
                    className="text-sm text-black p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{width: "250px"}}                    
                    dateFormat="dd-MM-yyyy"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-semibold text-white mb-1">To Date:</label>
                  <DatePicker
                    selected={toDate}
                    onChange={(date) => setToDate(date)}
                    className="text-sm text-black p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{width: "250px"}}
                    dateFormat="dd-MM-yyyy"
                  />
                </div>
              {[
                { label: "Student", value: student, setValue: setStudent },
                { label: "Teacher", value: teacher, setValue: setTeacher },
                { label: "Principal", value: principal, setValue: setPrincipal },
                { label: "Admin", value: admin, setValue: setAdmin },
                { label: "Session", value: session, setValue: setSession },
                { label: "Class", value: studentClass, setValue: setStudentClass },
                { label: "Section", value: section, setValue: setSection },
                { label: "Branch / College", value: branch, setValue: setBranch },
                { label: "Department", value: department, setValue: setDepartment },
                { label: "State", value: state, setValue: setState },
                { label: "Country", value: country, setValue: setCountry },
              ].map(({ label, value, setValue }, idx) => (
                <div key={idx} className="flex flex-col">
                  <label className="text-sm font-semibold text-white mb-1">{label}:</label>
                  <input
                    type="text"
                    name={label.toLowerCase().replace(" ", "")}
                    id={label.toLowerCase().replace(" ", "")}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    style={{width: "250px"}}
                    className="border rounded-md px-3 py-2 text-gray-800 focus:ring-2 focus:ring-blue-300 focus:outline-none"
                  />
                </div>
              ))}
            </div>
            <div className="mt-6 flex justify-end">
              <button 
                onClick={() => fetchDashboardData()}
                className="bg-red-600 text-white py-2 px-6 rounded-lg text-sm transition hover:bg-red-700 focus:ring-2 focus:ring-red-400 focus:outline-none">
                Apply Filter
              </button>
            </div>
          </div>
        )}
      </div>

      {isDummyClicked ? (
        <>
        <section className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-6 mb-6 p-4">
        {[
              { title: 'Total RCAs Conducted', value: '1,235', icon: 'faClipboardCheck' },
              { title: 'Pending RCAs', value: '245', icon: 'faExclamationCircle' },
              { title: 'Recurring Issues', value: '85', icon: 'faRedoAlt' },
              { title: 'Avg. Resolution Time', value: '5 Days', icon: 'faClock' },
          ].map((metric, index) => (
              <div
              key={index}
              className="flex flex-col items-center bg-black border border-blue-400 rounded-lg shadow-md p-6 text-center"
              >
              <i
                  className={`fas ${metric.icon} text-blue-500 text-3xl mb-4`}
                  aria-hidden="true"
              ></i>
              <h3 className="text-md font-semibold text-white">{metric.title}</h3>
              <p className="text-2xl font-bold text-blue-600">{metric.value}</p>
              </div>
          ))}
        </section>
        <section className="bg-black border border-blue-400 p-6 rounded-md shadow-md mb-6">
          <h2 className="text-xl font-bold mb-4 text-black">Branch/College Snapshot</h2>
          <table className="w-full table-auto border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-gray-300 p-2 text-left">Branch/College</th>
                <th className="border border-gray-300 p-2">Total Issues</th>
                <th className="border border-gray-300 p-2">Resolved</th>
                <th className="border border-gray-300 p-2">Pending</th>
                <th className="border border-gray-300 p-2">Avg. Resolution Time</th>
              </tr>
            </thead>
            <tbody>
              {[...Array(3)].map((_, idx) => (
                <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-100' : 'bg-gray-200'}>
                  <td className="border border-gray-300 p-2 text-black">Branch/College {idx + 1}</td>
                  <td className="border border-gray-300 p-2 text-black">{Math.floor(Math.random() * 500)}</td>
                  <td className="border border-gray-300 p-2 text-black">{Math.floor(Math.random() * 500)}</td>
                  <td className="border border-gray-300 p-2 text-black">{Math.floor(Math.random() * 200)}</td>
                  <td className="border border-gray-300 p-2 text-black">{Math.floor(Math.random() * 10) + 1} Days</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        <section className="bg-black border border-blue-400 p-6 rounded-md shadow-md mb-6">
          <h2 className="text-xl font-bold mb-4 text-black">Department Snapshot</h2>
          <table className="w-full table-auto border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-gray-300 p-2 text-left">Department</th>
                <th className="border border-gray-300 p-2">Total Issues</th>
                <th className="border border-gray-300 p-2">Resolved</th>
                <th className="border border-gray-300 p-2">Pending</th>
                <th className="border border-gray-300 p-2">Avg. Resolution Time</th>
              </tr>
            </thead>
            <tbody>
              {[...Array(3)].map((_, idx) => (
                <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-100' : 'bg-gray-200'}>
                  <td className="border border-gray-300 p-2 text-black">Department {idx + 1}</td>
                  <td className="border border-gray-300 p-2 text-black">{Math.floor(Math.random() * 500)}</td>
                  <td className="border border-gray-300 p-2 text-black">{Math.floor(Math.random() * 500)}</td>
                  <td className="border border-gray-300 p-2 text-black">{Math.floor(Math.random() * 200)}</td>
                  <td className="border border-gray-300 p-2 text-black">{Math.floor(Math.random() * 10) + 1} Days</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        <section className="bg-black border border-blue-400 p-6 rounded-md shadow-md mb-6">
          <h2 className="text-lg lg:text-xl font-bold mb-4 text-yellow-200">Class Snapshot</h2>
          <table className="w-full table-auto border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-gray-300 p-2 text-left">Class</th>
                <th className="border border-gray-300 p-2">Total Issues</th>
                <th className="border border-gray-300 p-2">Resolved</th>
                <th className="border border-gray-300 p-2">Pending</th>
                <th className="border border-gray-300 p-2">Avg. Resolution Time</th>
              </tr>
            </thead>
            <tbody>
              {[...Array(3)].map((_, idx) => (
                <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-100' : 'bg-gray-200'}>
                  <td className="border border-gray-300 p-2 text-black">Class {idx + 1}</td>
                  <td className="border border-gray-300 p-2 text-black">{Math.floor(Math.random() * 500)}</td>
                  <td className="border border-gray-300 p-2 text-black">{Math.floor(Math.random() * 500)}</td>
                  <td className="border border-gray-300 p-2 text-black">{Math.floor(Math.random() * 200)}</td>
                  <td className="border border-gray-300 p-2 text-black">{Math.floor(Math.random() * 10) + 1} Days</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        <section className="bg-black border border-blue-400 p-6 rounded-md shadow-md mb-6">
          <h2 className="text-lg lg:text-xl font-bold mb-4 text-yellow-200">Teacher Snapshot</h2>
          <table className="w-full table-auto border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-gray-300 p-2 text-left">Teacher</th>
                <th className="border border-gray-300 p-2">Total Issues</th>
                <th className="border border-gray-300 p-2">Resolved</th>
                <th className="border border-gray-300 p-2">Pending</th>
                <th className="border border-gray-300 p-2">Avg. Resolution Time</th>
              </tr>
            </thead>
            <tbody>
              {[...Array(3)].map((_, idx) => (
                <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-100' : 'bg-gray-200'}>
                  <td className="border border-gray-300 p-2 text-black">Teacher {idx + 1}</td>
                  <td className="border border-gray-300 p-2 text-black">{Math.floor(Math.random() * 500)}</td>
                  <td className="border border-gray-300 p-2 text-black">{Math.floor(Math.random() * 500)}</td>
                  <td className="border border-gray-300 p-2 text-black">{Math.floor(Math.random() * 200)}</td>
                  <td className="border border-gray-300 p-2 text-black">{Math.floor(Math.random() * 10) + 1} Days</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        <section className="bg-black border border-blue-400 p-6 rounded-md shadow-md mb-6">
          <h2 className="text-lg lg:text-xl font-bold mb-4 text-yellow-200">Trends</h2>
          <div className="grid grid-cols-2 sm:grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-100 rounded-md h-64 flex items-center justify-center">
                  <Bar data={dummyBarChartData} options={chartOptions} />
              </div>
              <div className="bg-gray-100 rounded-md h-64 flex items-center justify-center">
                  <Line data={dummyLineChartData} options={chartOptions} />
              </div>
          </div>
        </section>
        <section className="bg-black border border-blue-400 p-6 rounded-md shadow-md mb-6">
          <h2 className="text-xl font-bold mb-2 text-yellow-200">Priority Alerts</h2>
          <ul className="space-y-2">
              {[...Array(3)].map((_, idx) => (
              <li
                  key={idx}
                  className="border border-red-300 bg-black rounded-lg p-2 flex flex-wrap md:flex-nowrap items-center space-y-3 md:space-y-0"
              >
                  <div className="w-full md:w-3/4 text-sm text-white">
                  {idx + 1}. Problem Statement: Placeholder details
                  </div>
                  <div className="w-full md:w-1/4 text-right">
                  <button className="bg-red-600 text-white w-40 py-2 px-4 rounded-md text-sm transition hover:bg-red-700 focus:ring-2 focus:ring-red-400 focus:outline-none">
                      Action Required
                  </button>
                  </div>
              </li>
              ))}
          </ul>
        </section>
        <section className="bg-black border border-blue-400  p-6 rounded-md shadow-md">
          <h2 className="text-xl font-bold mb-4 text-yellow-200">Action Status</h2>
          <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
            <div className="bg-green-50 border border-green-300 rounded-md p-4">
              <h3 className="text-lg font-bold text-green-600">Completed Actions</h3>
              <ul className="list-disc ml-4 mt-2 text-gray-700">
                {[...Array(3)].map((_, idx) => (
                  <li key={idx}>Completed Action {idx + 1}</li>
                ))}
              </ul>
            </div>
            <div className="bg-yellow-50 border border-yellow-300 rounded-md p-4">
              <h3 className="text-lg font-bold text-yellow-600">Pending Actions</h3>
              <ul className="list-disc ml-4 mt-2 text-gray-700">
                {[...Array(3)].map((_, idx) => (
                  <li key={idx}>Pending Action {idx + 1}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>
        </>
      ):(
        <>
          <section className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-6 mb-6 p-4">
            {metrics.map((metric, index) => (
              <div
                key={index}
                className="flex flex-col items-center bg-black rounded-lg shadow-md p-6 text-center border border-blue-400"
              >
                <i
                  className={`fas ${metric.icon} text-blue-500 text-3xl mb-4`}
                  aria-hidden="true"
                ></i>
                <h3 className="text-md font-semibold text-white">{metric.title}</h3>
                <p
                    onClick={() => navigate("/my-concerns-list")}
                    className="text-2xl font-bold text-blue-600 underline cursor-pointer"
                  >
                    {metric.value}
                </p>
              </div>
            ))}
          </section>
          <section className="bg-black border border-blue-400 p-6 rounded-md shadow-md mb-6">
              <h2 className="text-lg lg:text-xl font-bold mb-4 text-yellow-200">Branch/College Snapshot</h2>
              <table className="w-full table-auto border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-200">
                    <th className="border border-gray-300 p-2 text-left">Branch/College</th>
                    <th className="border border-gray-300 p-2">Total Issues</th>
                    <th className="border border-gray-300 p-2">Resolved</th>
                    <th className="border border-gray-300 p-2">Pending</th>
                    <th className="border border-gray-300 p-2">Avg. Resolution Time</th>
                  </tr>
                </thead>
                <tbody>
                  {branchMetrics.length > 0 ? (
                    branchMetrics.map((bmetric, idx) => (
                      <tr
                        key={idx}
                        className={idx % 2 === 0 ? "bg-gray-100" : ""}
                      >
                        <td className="border border-gray-300 p-2 text-black">{bmetric.branch}</td>
                        <td className="border border-gray-300 p-2 text-black">{bmetric.totalIssues}</td>
                        <td className="border border-gray-300 p-2 text-black">{bmetric.resolved}</td>
                        <td className="border border-gray-300 p-2 text-black">{bmetric.pending}</td>
                        <td className="border border-gray-300 p-2 text-black">{bmetric.avgResolutionTime} Days</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        className="border border-gray-300 p-2 text-center text-gray-500"
                        colSpan="5"
                      >
                        No data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>
            <section className="bg-black border border-blue-400 p-6 rounded-md shadow-md mb-6">
              <h2 className="text-lg lg:text-xl font-bold mb-4 text-yellow-200">Department Snapshot</h2>
              <table className="w-full table-auto border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-200">
                    <th className="border border-gray-300 p-2 text-left">Department</th>
                    <th className="border border-gray-300 p-2">Total Issues</th>
                    <th className="border border-gray-300 p-2">Resolved</th>
                    <th className="border border-gray-300 p-2">Pending</th>
                    <th className="border border-gray-300 p-2">Avg. Resolution Time</th>
                  </tr>
                </thead>
                <tbody>
                  {departmentDMetrics.length > 0 ? (
                    departmentDMetrics.map((dmetric, idx) => (
                      <tr
                        key={idx}
                        className={idx % 2 === 0 ? "bg-gray-100" : "bg-gray-200"}
                      >
                        <td className="border border-gray-300 p-2 text-black">{dmetric.departmentName}</td>
                        <td className="border border-gray-300 p-2 text-black">{dmetric.totalIssues}</td>
                        <td className="border border-gray-300 p-2 text-black">{dmetric.resolved}</td>
                        <td className="border border-gray-300 p-2 text-black">{dmetric.pending}</td>
                        <td className="border border-gray-300 p-2 text-black">{dmetric.avgResolutionTime} Days</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        className="border border-gray-300 p-2 text-center text-gray-500"
                        colSpan="5"
                      >
                        No data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>
            <section className="bg-black border border-blue-400 p-6 rounded-md shadow-md mb-6">
              <h2 className="text-lg lg:text-xl font-bold mb-4 text-yellow-200">Class Snapshot</h2>
              <table className="w-full table-auto border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-200">
                    <th className="border border-gray-300 p-2 text-left">Class</th>
                    <th className="border border-gray-300 p-2">Total Issues</th>
                    <th className="border border-gray-300 p-2">Resolved</th>
                    <th className="border border-gray-300 p-2">Pending</th>
                    <th className="border border-gray-300 p-2">Avg. Resolution Time</th>
                  </tr>
                </thead>
                <tbody>
                  {studentClassMetrics.length > 0 ? (
                    studentClassMetrics.map((scmetric, idx) => (
                      <tr
                        key={idx}
                        className={idx % 2 === 0 ? "bg-gray-100" : "bg-gray-200"}
                      >
                        <td className="border border-gray-300 p-2 text-black">{scmetric.studentClass}</td>
                        <td className="border border-gray-300 p-2 text-black">{scmetric.totalIssues}</td>
                        <td className="border border-gray-300 p-2 text-black">{scmetric.resolved}</td>
                        <td className="border border-gray-300 p-2 text-black">{scmetric.pending}</td>
                        <td className="border border-gray-300 p-2 text-black">{scmetric.avgResolutionTime} Days</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        className="border border-gray-300 p-2 text-center text-gray-500"
                        colSpan="5"
                      >
                        No data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>
            <section className="bg-black border border-blue-400 p-6 rounded-md shadow-md mb-6">
              <h2 className="text-lg lg:text-xl font-bold mb-4 text-yellow-200">Teacher Snapshot</h2>
              <table className="w-full table-auto border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-200">
                    <th className="border border-gray-300 p-2 text-left">Teacher</th>
                    <th className="border border-gray-300 p-2">Total Issues</th>
                    <th className="border border-gray-300 p-2">Resolved</th>
                    <th className="border border-gray-300 p-2">Pending</th>
                    <th className="border border-gray-300 p-2">Avg. Resolution Time</th>
                  </tr>
                </thead>
                <tbody>
                  {teacherMetrics.length > 0 ? (
                    teacherMetrics.map((tmetric, idx) => (
                      <tr
                        key={idx}
                        className={idx % 2 === 0 ? "bg-gray-100" : "bg-gray-200"}
                      >
                        <td className="border border-gray-300 p-2 text-black">{tmetric.teacher}</td>
                        <td className="border border-gray-300 p-2 text-black">{tmetric.totalIssues}</td>
                        <td className="border border-gray-300 p-2 text-black">{tmetric.resolved}</td>
                        <td className="border border-gray-300 p-2 text-black">{tmetric.pending}</td>
                        <td className="border border-gray-300 p-2 text-black">{tmetric.avgResolutionTime} Days</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        className="border border-gray-300 p-2 text-center text-gray-500"
                        colSpan="5"
                      >
                        No data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>
            <section className="bg-black p-6 rounded-md shadow-md mb-6">
              <h2 className="text-lg lg:text-xl font-bold mb-4 text-yellow-200">Trends</h2>
              <div className="grid grid-cols-2 sm:grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-100 rounded-md h-64 flex items-center justify-center">
                    <Bar data={barChartData} options={chartOptions} />
                </div>
                <div className="bg-gray-100 rounded-md h-64 flex items-center justify-center">
                    <Line data={lineChartData} options={chartOptions} />
                </div>
              </div>
            </section>
            <section className="bg-black border border-blue-400 p-6 rounded-md shadow-md mb-6">
              <h2 className="text-lg lg:text-xl font-bold mb-2 text-yellow-200">
                Priority Alerts {'3 of '}
                <span
                    onClick={() => navigate("/my-concerns-list")}
                    className="text-blue-500 hover:underline cursor-pointer"
                  >
                    {dashboardData.filter((alert) => alert.isPriority === true).length}
                </span>
              </h2>
              <ul className="space-y-2">
                {dashboardData.length > 0 ? (
                  dashboardData.slice(0, 3).map((alert, idx) => (
                    <li
                      key={alert._id}
                      className="border border-red-300 bg-black rounded-lg p-2 flex flex-wrap md:flex-nowrap items-center space-y-3 md:space-y-0"
                    >
                      <div className="w-3/4 text-sm text-white">
                        {idx + 1}. {alert.problemStatement || "Details not available"}
                      </div>
                      <div className="w-1/4 text-right">
                        <button className="bg-red-600 text-white w-40 py-2 px-4 rounded-md text-sm transition hover:bg-red-700 focus:ring-2 focus:ring-red-400 focus:outline-none">
                          Action Required
                        </button>
                      </div>
                    </li>
                  ))
                ) : (
                  <li className="text-gray-500 text-center">No alerts available</li>
                )}
              </ul>
            </section>

            <section className="bg-black border border-blue-400 p-6 rounded-md shadow-md">
              <h2 className="text-lg lg:text-xl font-bold mb-4 text-yellow-200">Action Status</h2>
              <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
                {/* Completed Actions */}
                <div className="bg-green-50 border border-green-300 rounded-md p-4">
                  <h3 className="text-lg font-bold text-green-600">
                    Completed Actions {'3 of '}
                    <span
                      onClick={() => navigate("/my-concerns-list")}
                      className="text-blue-500 hover:underline cursor-pointer"
                    >
                      {dashboardData.filter((completed) => completed.isCompleted === true).length}
                    </span>
                  </h3>
                  <ul className="list-disc ml-4 mt-2 text-gray-700">
                    {dashboardData.filter((item) => item.isCompleted).length > 0 ? (
                      dashboardData
                        .filter((item) => item.isCompleted)
                        .slice(0, 3) // Show max 3 completed actions
                        .map((item, idx) => (
                          <li key={item._id}>{item.problemStatement || `Completed Action ${idx + 1}`}</li>
                        ))
                    ) : (
                      <li className="text-gray-500">No completed actions available</li>
                    )}
                  </ul>
                </div>

                {/* Pending Actions */}
                <div className="bg-yellow-50 border border-yellow-300 rounded-md p-4">
                  <h3 className="text-lg font-bold text-yellow-600">
                    Pending Actions {'3 of '}
                    <span
                      onClick={() => navigate("/my-concerns-list")}
                      className="text-blue-500 hover:underline cursor-pointer"
                    >
                      {dashboardData.filter((pending) => pending.isCompleted === false).length}
                    </span>
                  </h3>
                  <ul className="list-disc ml-4 mt-2 text-gray-700">
                    {dashboardData.filter((item) => !item.isCompleted).length > 0 ? (
                      dashboardData
                        .filter((item) => !item.isCompleted)
                        .slice(0, 3) // Show max 3 pending actions
                        .map((item, idx) => (
                          <li key={item._id}>{item.problemStatement || `Pending Action ${idx + 1}`}</li>
                        ))
                    ) : (
                      <li className="text-gray-500">No pending actions available</li>
                    )}
                  </ul>
                </div>
              </div>
            </section>
        </>
      )}
    </div>
  );
};

export default InspireDashboard;
